import React from 'react';
import { motion } from 'framer-motion';
import {
    AlertCircle,
    Shield,
    Coins,
    Activity,
    Network,
    Heart,
    Home,
    GraduationCap,
    Scale,
    Layers,
    Users,
    Target,
    TrendingUp
} from 'lucide-react';
import PageHero from '../components/layout/PageHero';
import SEO from '../components/SEO';
import RichTextRenderer from '../components/content/RichTextRenderer';
import content from '../data/how_it_works_content.json';
import './HowItWorks.css';

const ICON_MAP = {
    1: AlertCircle,
    2: Shield,
    3: Coins,
    4: Activity,
    5: Network,
    6: Heart,
    7: Home,
    8: GraduationCap,
    9: Scale,
    10: Layers,
    11: Users,
    12: Target,
    13: TrendingUp
};

const Section = ({ section }) => {
    const Icon = ICON_MAP[section.number] || Target;

    return (
        <motion.section
            id={section.id}
            className={`how-section ${section.id === 'confronts' ? 'confronts-section' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
        >
            <div className="section-header">
                <div className="section-number-badge">
                    <span>{section.number}</span>
                </div>
                <h2><Icon className="section-icon" /> {section.title}</h2>
            </div>

            <div className="section-body">
                {section.content && <RichTextRenderer content={section.content} />}

                {section.subparts && (
                    <div className="subparts-container">
                        {section.subparts.map((sub, i) => (
                            <div key={i} className="subpart-block">
                                <h3 className="subpart-title">{sub.title}</h3>
                                <RichTextRenderer content={sub.content} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {section.interstitial_image && (
                <motion.div
                    className="interstitial-image-container media-frame"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                >
                    <img src={section.interstitial_image} alt={section.title} className="interstitial-image" />
                </motion.div>
            )}
        </motion.section>
    );
};

export default function HowItWorks() {
    return (
        <div className="how-it-works-page">
            <SEO
                title={`${content.title} | SAFEC+`}
                description={content.subtitle}
                image="/image/home_hero_cinematic.jpg"
            />

            <PageHero
                title={content.title}
                subtitle={content.subtitle}
                image={content.hero_image || "/image/home_hero_cinematic.jpg"}
                badge="Full Legislative Overview"
            />

            <div className="container">
                <div className="how-intro glass-panel">
                    <p>The SAFECARE Act represents a fundamental shift in the American healthcare economy. This document provides the complete, integrated overview of the bill's mechanics, impact, and the structural reforms it implements.</p>
                </div>

                <div className="sections-container">
                    {content.sections.map((section) => (
                        <Section key={section.number} section={section} />
                    ))}
                </div>

                <div className="how-footer glass-panel">
                    <h2>Built for Resilience.</h2>
                    <p>By structuring the reform as interlocking but modular changes, SAFEC+ ensures that progress can be defended and expanded over time.</p>
                </div>
            </div>
        </div>
    );
}
